import { Card, Segmented, Skeleton, Typography } from "antd";
import type { EChartsOption } from "echarts";
import ReactECharts from "echarts-for-react";
import type { Currency } from "stores/localeStore";
import { formatCurrencyAmount } from "utility/formatCurrency";
import type { RevenueMarketFilter, RevenueTimeseriesPoint } from "../types";

const { Text } = Typography;

const VB_BLUE = "#195178";
const LINE_COLOR = "#faad14";

const fmtSv = (value: number) => new Intl.NumberFormat("sv-SE").format(value);

const MONTH_LABELS_SV = [
  "jan",
  "feb",
  "mar",
  "apr",
  "maj",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "dec",
] as const;

const MARKET_OPTIONS: { label: string; value: string }[] = [
  { label: "Alla marknader", value: "all" },
  { label: "SEK", value: "SEK" },
  { label: "NOK", value: "NOK" },
  { label: "DKK", value: "DKK" },
];

function periodToMonthLabel(period: string): string {
  const month = Number(period.split("-")[1]);
  if (!month || month < 1 || month > 12) {
    return period;
  }
  return MONTH_LABELS_SV[month - 1];
}

function formatRevenue(value: number, currency: RevenueMarketFilter): string {
  if (currency) {
    return formatCurrencyAmount(value, currency as Currency);
  }
  return `${fmtSv(Math.round(value))} kr`;
}

function buildOption(
  points: RevenueTimeseriesPoint[],
  currency: RevenueMarketFilter,
): EChartsOption {
  const categories = points.map((p) => periodToMonthLabel(p.period));
  const revenue = points.map((p) => p.revenue);
  const orderCounts = points.map((p) => p.orderCount);

  return {
    grid: { left: 8, right: 8, top: 30, bottom: 4, containLabel: true },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params) => {
        const items = Array.isArray(params) ? params : [params];
        const first = items[0] as { name?: string; axisValue?: string | number } | undefined;
        const title = String(first?.axisValue ?? first?.name ?? "");
        const lines = items.map((item) => {
          const raw = Number(item.value ?? 0);
          const formatted =
            item.seriesName === "Omsättning"
              ? formatRevenue(raw, currency)
              : fmtSv(raw);
          return `${item.marker ?? ""}${item.seriesName}: ${formatted}`;
        });
        return [title, ...lines].join("<br/>");
      },
    },
    legend: {
      data: ["Omsättning", "Ordrar"],
      right: 0,
      top: 0,
      icon: "roundRect",
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: "rgba(0,0,0,0.65)" },
    },
    xAxis: {
      type: "category",
      data: categories,
      axisLine: { lineStyle: { color: "#f0f0f0" } },
      axisTick: { show: false },
      axisLabel: { color: "rgba(0,0,0,0.45)" },
    },
    yAxis: [
      {
        type: "value",
        name: "Omsättning",
        nameTextStyle: { color: "rgba(0,0,0,0.45)" },
        splitLine: { lineStyle: { color: "#f5f5f5" } },
        axisLabel: {
          color: "rgba(0,0,0,0.45)",
          formatter: (value: number) => fmtSv(value),
        },
      },
      {
        type: "value",
        name: "Ordrar",
        nameTextStyle: { color: "rgba(0,0,0,0.45)" },
        splitLine: { show: false },
        axisLabel: { color: "rgba(0,0,0,0.45)", formatter: fmtSv },
      },
    ],
    series: [
      {
        name: "Omsättning",
        type: "bar",
        data: revenue,
        barMaxWidth: 26,
        itemStyle: {
          color: VB_BLUE,
          borderRadius: [4, 4, 0, 0],
        },
      },
      {
        name: "Ordrar",
        type: "line",
        yAxisIndex: 1,
        data: orderCounts,
        smooth: true,
        showSymbol: false,
        lineStyle: { width: 2, color: LINE_COLOR },
        itemStyle: { color: LINE_COLOR },
      },
    ],
  };
}

export type RevenuePerMonthChartProps = {
  points?: RevenueTimeseriesPoint[];
  loading?: boolean;
  market: RevenueMarketFilter;
  onMarketChange: (market: RevenueMarketFilter) => void;
  onMonthClick?: (period: string) => void;
};

export function RevenuePerMonthChart({
  points = [],
  loading = false,
  market,
  onMarketChange,
  onMonthClick,
}: RevenuePerMonthChartProps) {
  const option = buildOption(points, market);

  return (
    <Card
      bordered={false}
      className="criclebox"
      headStyle={{ padding: "1rem 1rem 0", borderBottom: "none" }}
      bodyStyle={{ padding: "1rem" }}
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontWeight: 600,
                fontSize: 16,
                color: "rgba(0,0,0,0.85)",
              }}
            >
              Omsättning per månad
            </div>
            <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
              Orderintäkter (OrderTotal) och antal ordrar från Jetshop
            </Text>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 8,
            }}
          >
            <Segmented
              size="small"
              options={MARKET_OPTIONS}
              value={market ?? "all"}
              onChange={(value) => {
                const next = String(value);
                onMarketChange(next === "all" ? null : (next as Currency));
              }}
            />
            <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
              Rullande 12 månader
            </Text>
          </div>
        </div>
      }
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 8 }} title={false} />
      ) : (
        <div style={{ paddingTop: "1rem" }}>
          <ReactECharts
            option={option}
            style={{ height: 360, width: "100%", cursor: onMonthClick ? "pointer" : undefined }}
            opts={{ renderer: "canvas" }}
            notMerge
            onEvents={
              onMonthClick
                ? {
                    click: (params: { dataIndex?: number }) => {
                      const index = params.dataIndex;
                      if (index == null || index < 0 || index >= points.length) {
                        return;
                      }
                      onMonthClick(points[index].period);
                    },
                  }
                : undefined
            }
          />
        </div>
      )}
    </Card>
  );
}
